import React, { useState, useEffect, useRef } from "react";
import { useTender } from "../../../contexts/TenderContext";
import MainLayout from "../../layout/MainLayout";
import { Card, Table, Input, Button, Typography, Space, message, Tooltip } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ColumnConfigModal } from "../../common/ColumnConfigModal";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import NumberInput from "../../common/NumberInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes } from '@/utils/addColumnCodes';
import { GARDEN_REFERENCES } from '@/utils/gardenReferenceMapping';
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";

const { Title, Text } = Typography;

interface GardenRow {
  key: string;
  plotNo: string;
  housetype: string;
  gardenArea: number;
  scaffoldPlatform: number;
  thickNoDigLayer: number;
  geotextileLayer: number;
  spreadLevelCleanSubsoilCapSiteWon: number;
  spreadLevelCleanSubsoilCapImported: number;
  harrowGardens: number;
  plotClearance: number;
  spreadLevelTopsoilSiteWon: number;
  spreadLevelTopsoilImported: number;
}

const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "House Type" },
  { key: "scaffoldPlatform", title: "Scaffold Platform (m²)" },
  { key: "thickNoDigLayer", title: "xxxmm thick 'no dig' layer (m²)" },
  { key: "geotextileLayer", title: "Geotextile Layer (m²)" },
  {
    key: "spreadLevelCleanSubsoilCapSiteWon",
    title: "Spread and level xxxmm thick site won clean subsoil capping layer (m²)",
  },
  {
    key: "spreadLevelCleanSubsoilCapImported",
    title: "Spread and level xxxmm thick imported clean subsoil capping layer (m²)",
  },
  { key: "harrowGardens", title: "Harrow gardens to 300mm depth (m²)" },
  { key: "plotClearance", title: "Remove general builders rubbish prior to topsoiling - if separate (m²)" },
  {
    key: "spreadLevelTopsoilSiteWon",
    title: "Spread and level xxxmm thick site won topsoil (m²)",
  },
  {
    key: "spreadLevelTopsoilImported",
    title: "Spread and level xxxmm thick imported topsoil (m²)",
  },
];

const Garden: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Format column title to display field name and unit separately
  const formatColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+\((m[²³]|m|Nr|t)\)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+\((m[²³]|m|Nr|t)\)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    []
  );

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<GardenRow[]>([]);
  // Defers the Garden table's first mount by one paint cycle so a tender
  // with a lot of plots doesn't make this step look frozen while the
  // table's initial render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedGardenKeys, setSelectedGardenKeys] = useState<React.Key[]>([]);
  const [columnHeaders, setColumnHeaders] = useState<Record<string, string>>(
    {}
  );
  const [dimensionValues, setDimensionValues] = useState<
    Record<string, string>
  >({});
  // Temporary state for pending header changes (only applied when user clicks Apply)
  const [tempDimensionValues, setTempDimensionValues] = useState<
    Record<string, string>
  >({});
  const [tempColumnHeaders, setTempColumnHeaders] = useState<
    Record<string, string>
  >({});
  const [tableKey, setTableKey] = useState(0);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Ref to track latest groundworks state for save operations
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // Track previous configModalVisible state to detect when modal opens
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Reset temp values when modal opens (configModalVisible goes from false to true)
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      // Modal just opened, reset temp values to current values
      setTempDimensionValues({ ...dimensionValues });
      setTempColumnHeaders({ ...columnHeaders });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, dimensionValues, columnHeaders]);

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<GardenRow[]>({
    pageId: "garden",
    onChangeDetected: (changes) => {
    },
  }); */

  useEffect(() => {
    if (tender?.groundworks?.gardenTable) {
      setData(tender.groundworks.gardenTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.gardenTable]);

  // Load dimension values and column headers from tender context on mount
  useEffect(() => {
    if (tender?.groundworks?.gardenDimensionValues) {
      setDimensionValues(tender.groundworks.gardenDimensionValues);
    }
    if (tender?.groundworks?.gardenColumnHeaders) {
      setColumnHeaders(tender.groundworks.gardenColumnHeaders);
    }
    // Mark initial load as complete after a short delay
    const timer = setTimeout(() => {
      setIsInitialLoadComplete(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Sync dimension values and column headers to tender context whenever they change (no auto-save; save is triggered explicitly in handleConfigApply)
  useEffect(() => {
    if (isInitialLoadComplete) {
      const currentGroundworks = groundworksRef.current || {};
      updateField("groundworks", {
        ...currentGroundworks,
        gardenColumnHeaders: columnHeaders,
        gardenDimensionValues: dimensionValues,
      });
    }
  }, [columnHeaders, dimensionValues, isInitialLoadComplete]);

  // Apply configure-headers modal: save temp values directly to context + API
  const handleConfigApply = () => {
    setDimensionValues(tempDimensionValues);
    setColumnHeaders(tempColumnHeaders);

    const currentGroundworks = groundworksRef.current || {};
    updateField("groundworks", {
      ...currentGroundworks,
      gardenColumnHeaders: tempColumnHeaders,
      gardenDimensionValues: tempDimensionValues,
    });
    setTimeout(() => saveTender(), 100);
  };

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<GardenRow>({
    filename: "Garden",
    sheetName: "Garden",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        gardenTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];


          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No")
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }


          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];

          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Skip only the first row (totals row)
          const dataRows = allDataRows.slice(1);


          // Find basic column indices
          const plotNoIdx = headers.findIndex(
            (h: any) => String(h).includes("Plot") && String(h).includes("No")
          );
          const housetypeIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("house") &&
              String(h).toLowerCase().includes("type")
          );
          const scaffoldPlatformIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("scaffold") &&
              String(h).toLowerCase().includes("platform")
          );

          // Build column mapping using sequential search
          const columnMapping: { [key: string]: number } = {};
          let currentSearchIndex = 0;

          excelColumns.forEach((col, colArrayIndex) => {
            // For basic columns, use direct findIndex
            if (colArrayIndex < 3) {
              if (col.key === "plotNo") {
                columnMapping[col.key] = plotNoIdx;
              } else if (col.key === "housetype") {
                columnMapping[col.key] = housetypeIdx;
              } else if (col.key === "scaffoldPlatform") {
                columnMapping[col.key] = scaffoldPlatformIdx;
              }
              currentSearchIndex = columnMapping[col.key] + 1;
            } else {
              // For other columns, search sequentially from currentSearchIndex
              const foundIndex = headers.findIndex((h: any, idx: number) => {
                if (idx < currentSearchIndex) return false;

                const headerText = String(h || "")
                  .toLowerCase()
                  .trim();
                const titleText = col.title.toLowerCase();

                // Match based on the column title, flexible with dimensions
                if (/(x{2,}mm?)/i.test(col.title)) {
                  // Dynamic column - match without dimension pattern
                  const patternWithoutDimension = titleText
                    .replace(/(x{2,}mm?)/gi, "")
                    .replace(/\(.*?\)/g, "")
                    .trim();
                  const headerWithoutParens = headerText
                    .replace(/\(.*?\)/g, "")
                    .trim();

                  const patternWords = patternWithoutDimension
                    .split(/\s+/)
                    .filter((w) => w.length > 0);
                  const match = patternWords.every((word) =>
                    headerWithoutParens.includes(word)
                  );

                  return match;
                } else {
                  // Non-dynamic column - exact matching
                  const titleWords = titleText
                    .replace(/\(.*?\)/g, "")
                    .trim()
                    .split(/\s+/);
                  return titleWords.every((word) => headerText.includes(word));
                }
              });

              if (foundIndex >= 0) {
                columnMapping[col.key] = foundIndex;
                currentSearchIndex = foundIndex + 1;
              } else {
              }
            }
          });


          // Extract column headers and dimension values
          const extractedHeaders: Record<string, string> = {};
          const extractedDimensionValues: Record<string, string> = {};

          // Build multi-row headers to search (up to 4 rows below headerRowIndex)
          const headerRowsToSearch: string[][] = [];
          for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
            if (jsonData[r]) {
              headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
            }
          }

          // Define fixed text for each dynamic column to extract only the leading dimension
          const dimensionFixedText: Record<string, string> = {
            thickNoDigLayer: "thick",
            spreadLevelCleanSubsoilCapSiteWon: "thick site won clean subsoil capping layer",
            spreadLevelCleanSubsoilCapImported: "thick imported clean subsoil capping layer",
            spreadLevelTopsoilSiteWon: "thick site won topsoil",
            spreadLevelTopsoilImported: "thick imported topsoil",
          };

          excelColumns.forEach((col) => {
            const actualColumnIndex = columnMapping[col.key];
            if (actualColumnIndex !== undefined && actualColumnIndex >= 0) {
              const headerValue = String(
                headers[actualColumnIndex] || ""
              ).trim();
              extractedHeaders[col.key] = headerValue;

              // Check if this column has dimension patterns
              if (/(x{2,}mm?)/i.test(col.title)) {
                // Search across multiple header rows for the dimension value
                const fixedText = dimensionFixedText[col.key];
                let found = false;

                for (const headerRow of headerRowsToSearch) {
                  if (found) break;
                  const cellValue = headerRow[actualColumnIndex];
                  if (!cellValue) continue;

                  if (fixedText) {
                    // Find the fixed text portion and extract the leading dimension before it
                    const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
                    if (fixedTextIndex > 0) {
                      const leadingText = cellValue.substring(0, fixedTextIndex).trim();
                      // Extract numeric dimension (supports decimals like 12.34)
                      const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
                      if (dimMatch) {
                        extractedDimensionValues[col.key] = dimMatch[1];
                        found = true;
                      }
                    }
                  }

                  if (!found) {
                    // Fallback: extract first number followed by mm
                    const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
                    if (dimensionMatch) {
                      extractedDimensionValues[col.key] = dimensionMatch[1];
                      found = true;
                    }
                  }
                }
              }
            }
          });


          setColumnHeaders(extractedHeaders);
          setDimensionValues(extractedDimensionValues);

          // Get column indices from mapping
          const noDigLayerIdx = columnMapping["thickNoDigLayer"] ?? -1;
          const geotextileIdx = columnMapping["geotextileLayer"] !== undefined
            ? columnMapping["geotextileLayer"]
            : headers.findIndex((h: unknown) =>
                String(h || "").toLowerCase().includes("geotextile") &&
                String(h || "").toLowerCase().includes("layer"),
              );
          const cleanSubsoilSiteWonIdx =
            columnMapping["spreadLevelCleanSubsoilCapSiteWon"] ?? -1;
          const cleanSubsoilImportedIdx =
            columnMapping["spreadLevelCleanSubsoilCapImported"] ?? -1;
          const harrowIdx = columnMapping["harrowGardens"] ?? -1;
          const plotClearanceIdx = columnMapping["plotClearance"] ?? -1;
          const topsoilSiteWonIdx =
            columnMapping["spreadLevelTopsoilSiteWon"] ?? -1;
          const topsoilImportedIdx =
            columnMapping["spreadLevelTopsoilImported"] ?? -1;


          const allImportedRows: GardenRow[] = dataRows
            .filter(
              (row) =>
                row &&
                row.length > 0 &&
                row.some(
                  (cell) => cell !== null && cell !== undefined && cell !== ""
                )
            )
            .map((row, index) => {
              return {
                key: `imported_${Date.now()}_${index}`,
                plotNo: String(row[plotNoIdx] || ""),
                housetype: String(row[housetypeIdx] || ""),
                gardenArea: 0,
                scaffoldPlatform: Number(row[scaffoldPlatformIdx] || 0),
                thickNoDigLayer: Number(row[noDigLayerIdx] || 0),
                geotextileLayer: Number(row[geotextileIdx] || 0),
                spreadLevelCleanSubsoilCapSiteWon: Number(
                  row[cleanSubsoilSiteWonIdx] || 0
                ),
                spreadLevelCleanSubsoilCapImported: Number(
                  row[cleanSubsoilImportedIdx] || 0
                ),
                harrowGardens: Number(row[harrowIdx] || 0),
                plotClearance: Number(row[plotClearanceIdx] || 0),
                spreadLevelTopsoilSiteWon: Number(row[topsoilSiteWonIdx] || 0),
                spreadLevelTopsoilImported: Number(
                  row[topsoilImportedIdx] || 0
                ),
              };
            })
            .filter((row) => {
              const plotNoStr = String(row.plotNo || "").trim();
              const hasPlotNo =
                plotNoStr !== "" &&
                plotNoStr !== "0" &&
                plotNoStr !== "null" &&
                plotNoStr !== "undefined";
              const housetypeStr = String(row.housetype || "").trim();
              const hasValidHouseType =
                housetypeStr !== "" &&
                !housetypeStr.toLowerCase().includes("total") &&
                !housetypeStr.toLowerCase().includes("sum") &&
                isNaN(Number(housetypeStr)) && // Not purely numeric
                housetypeStr.match(/[a-zA-Z]/); // Contains letters
              return hasPlotNo && hasValidHouseType;
            });


          if (allImportedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            gardenTable: allImportedRows,
          });

          // ============================================================
          // AUTO-SAVE DISABLED - Commented out for now
          // ============================================================
          // // Trigger immediate save after import
          // const { triggerImmediateSave } = await import(
          //   "../../../utils/storage"
          // );
          // triggerImmediateSave(allImportedRows.length);

          alert(`Successfully imported ${allImportedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  const createEmptyRow = (key?: string): GardenRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    gardenArea: 0,
    scaffoldPlatform: 0,
    thickNoDigLayer: 0,
    geotextileLayer: 0,
    spreadLevelCleanSubsoilCapSiteWon: 0,
    spreadLevelCleanSubsoilCapImported: 0,
    harrowGardens: 0,
    plotClearance: 0,
    spreadLevelTopsoilSiteWon: 0,
    spreadLevelTopsoilImported: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof GardenRow,
    value: string | number
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        gardenTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      gardenTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedGardenKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Garden Bill Summary — see CutFills.tsx's identical fix
      // for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        gardenTable: newData,
        ...(newData.length === 0 && { gardenExtraItems: {} }),
      });
      setSelectedGardenKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and continue from Garden", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/retaining-walls"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and exit from Garden", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  // Helper function to get column title - uses dynamic header if available, otherwise falls back to default
  const getColumnTitle = React.useCallback(
    (fieldKey: string, defaultTitle: string): string => {
      // Return stored column header (from Excel import) exactly as-is.
      if (columnHeaders[fieldKey]) {
        return columnHeaders[fieldKey];
      }

      // Fallback: substitute dimension value into the default title pattern and
      // normalise any decimal-mm result (e.g. "0.150mm" → "150mm").
      if (dimensionValues[fieldKey] && dimensionValues[fieldKey].trim() !== "") {
        let title = defaultTitle;
        title = title.replace(/(x{2,}mm?)/gi, (match) => {
          let value = dimensionValues[fieldKey];
          if (
            match.toLowerCase().includes("mm") &&
            !value.toLowerCase().endsWith("mm")
          ) {
            value = value + "mm";
          }
          return value;
        });
        return normaliseDecimalMmInLabel(title);
      }

      return defaultTitle;
    },
    [columnHeaders, dimensionValues]
  );

  // Extract dimension fields from excel columns
  const dimensionFields = React.useMemo(() => {
    const fieldsWithDimensions: Array<{ key: string; title: string }> = [];

    // Check excelColumns for fields with dimension patterns (xxmm, xxxmm, etc.)
    excelColumns.forEach((col) => {
      const defaultTitle = col.title;
      // Check if DEFAULT header contains dimension patterns like xxmm, xxxmm
      if (/(x{2,}mm?)/i.test(String(defaultTitle))) {
        fieldsWithDimensions.push({
          key: col.key,
          title: String(defaultTitle), // Always use default title
        });
      }
    });

    return fieldsWithDimensions;
  }, []);

  const getScrollConfig = (columns: { width?: number | string }[]) => {
    const totalWidth = columns.reduce((sum, col) => sum + (Number(col.width) || 120), 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const calculateTotals = () => {
    const totals: Partial<GardenRow> = {
      scaffoldPlatform: 0,
      thickNoDigLayer: 0,
      geotextileLayer: 0,
      spreadLevelCleanSubsoilCapSiteWon: 0,
      spreadLevelCleanSubsoilCapImported: 0,
      harrowGardens: 0,
      plotClearance: 0,
      spreadLevelTopsoilSiteWon: 0,
      spreadLevelTopsoilImported: 0,
    };

    data.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += Math.round((Number(row[key]) || 0) * 1000) / 1000;
      });
    });

    return totals;
  };

  const columns = [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: GardenRow) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) => {
            if (isDuplicatePlotNo(data, record.key, newValue)) {
              message.error(`Plot No "${newValue}" is already in use.`);
              return;
            }
            handleInputChange(record.key, "plotNo", newValue);
          }}
          placeholder="Plot No"
        />
      ),
    },
    {
      title: "House type",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: GardenRow) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(record.key, "housetype", e.target.value)
          }
          placeholder="House type"
          size="small"
        />
      ),
    },
    {
      title: formatColumnTitle(getColumnTitle("thickNoDigLayer", "xxxmm thick 'no dig' layer (m²)")),
      dataIndex: "thickNoDigLayer",
      key: "thickNoDigLayer",
      width: 150,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "thickNoDigLayer", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Geotextile Layer (m²)"),
      dataIndex: "geotextileLayer",
      key: "geotextileLayer",
      width: 120,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "geotextileLayer", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(getColumnTitle(
        "spreadLevelCleanSubsoilCapSiteWon",
        "Spread and level xxxmm thick site won clean subsoil capping layer (m²)"
      ) as string),
      dataIndex: "spreadLevelCleanSubsoilCapSiteWon",
      key: "spreadLevelCleanSubsoilCapSiteWon",
      width: 200,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "spreadLevelCleanSubsoilCapSiteWon",
              newValue
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(getColumnTitle(
        "spreadLevelCleanSubsoilCapImported",
        "Spread and level xxxmm thick imported clean subsoil capping layer (m²)"
      ) as string),
      dataIndex: "spreadLevelCleanSubsoilCapImported",
      key: "spreadLevelCleanSubsoilCapImported",
      width: 200,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "spreadLevelCleanSubsoilCapImported",
              newValue
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Harrow gardens to 300mm depth (m²)"),
      dataIndex: "harrowGardens",
      key: "harrowGardens",
      width: 120,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "harrowGardens", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle("Remove general builders rubbish prior to topsoiling - if separate (m²)"),
      dataIndex: "plotClearance",
      key: "plotClearance",
      width: 120,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "plotClearance", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(getColumnTitle(
        "spreadLevelTopsoilSiteWon",
        "Spread and level xxxmm thick site won topsoil (m²)"
      ) as string),
      dataIndex: "spreadLevelTopsoilSiteWon",
      key: "spreadLevelTopsoilSiteWon",
      width: 200,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(record.key, "spreadLevelTopsoilSiteWon", newValue)
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: formatColumnTitle(getColumnTitle(
        "spreadLevelTopsoilImported",
        "Spread and level xxxmm thick imported topsoil (m²)"
      ) as string),
      dataIndex: "spreadLevelTopsoilImported",
      key: "spreadLevelTopsoilImported",
      width: 200,
      render: (value: number, record: GardenRow) => (
        <NumberInput
          value={value || 0}
          onChange={(newValue) =>
            handleInputChange(
              record.key,
              "spreadLevelTopsoilImported",
              newValue
            )
          }
          placeholder="0.000"
          decimalPlaces={3}
          size="small"
          style={{ textAlign: "right" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: GardenRow) => (
        <Button
          type="text"
          danger
          icon={
            <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
          }
          onClick={() => deleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm garden-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header */}
        <div
          className="garden-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Garden
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Garden preparation and landscaping calculations for
                  residential plots.
                </Text>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {selectedGardenKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedGardenKeys.length})
                  </Button>
                )}
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setConfigModalVisible(true)}
                  type="default"
                  className="configure-headers-btn"
                  style={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginTop: "18px",
                  }}
                >
                  Configure Headers
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div
          className="px-6 pb-6 garden-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          <div>
            <div
              className="garden-sticky-dimension"
              style={{
                position: "sticky",
                top:
                  144 +
                  document
                    .getElementsByClassName("garden-sticky-header")?.[0]
                    ?.getBoundingClientRect?.()?.height +
                  "px",
                backgroundColor: "#fff",
                zIndex: 98,
              }}
            ></div>

            {isTableLoading ? (
              <TableLoadingPlaceholder />
            ) : (
            <Table
              key={tableKey}
              dataSource={data}
              columns={addColumnCodes(columns, GARDEN_REFERENCES)}
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedGardenKeys,
                onChange: setSelectedGardenKeys,
                fixed: "left",
                columnWidth: 48,
              }}
              scroll={getScrollConfig(columns)}
              sticky={{
                offsetHeader:
                  144 +
                  document
                    .getElementsByClassName("garden-sticky-header")?.[0]
                    ?.getBoundingClientRect?.()?.height +
                  document
                    .getElementsByClassName("garden-sticky-dimension")?.[0]
                    ?.getBoundingClientRect?.()?.height,
              }}
              size="middle"
              bordered
              className="garden-table"
              summary={() => {
                if (data.length === 0) return null;
                const totals = calculateTotals();
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                    >
                      <Table.Summary.Cell index={0} colSpan={3} align="center">
                        <span style={{ color: "#000" }}>Totals</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.thickNoDigLayer?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.geotextileLayer?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.spreadLevelCleanSubsoilCapSiteWon?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.spreadLevelCleanSubsoilCapImported?.toFixed(
                            2
                          )}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.harrowGardens?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.plotClearance?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.spreadLevelTopsoilSiteWon?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.spreadLevelTopsoilImported?.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
            )}
            <div style={{ marginTop: "16px" }}>
              <Button
                type="text"
                icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                onClick={addRow}
                style={{
                  color: COLORS.primary,
                  fontWeight: 500,
                  padding: "4px 8px",
                }}
              >
                Add New Row
              </Button>
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden Excel Import/Export Component */}
      <div style={{ display: "none" }}>
        <ExcelImportExport
          onExport={handleExportExcel}
          onImport={handleImportExcel}
          onDownloadTemplate={handleDownloadTemplate}
          importLoading={importLoading}
          importModalVisible={importModalVisible}
          setImportModalVisible={setImportModalVisible}
          hasData={data.length > 0}
        />
      </div>

      <style>{`
        .garden-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .garden-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .garden-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .garden-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .garden-card .ant-card-body {
          overflow: visible !important;
        }

        .garden-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .garden-table .ant-table-body {
          margin-top: 0 !important;
        }

        .garden-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .garden-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .garden-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: 1px solid #e5e7eb !important;
        }

        .garden-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .garden-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .garden-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .garden-table .ant-table-thead > tr > th.ant-table-selection-column,
        .garden-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .garden-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .garden-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .garden-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .garden-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .garden-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        .garden-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .garden-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude first 2 columns: Plot No, Housetype) */
        .garden-table td:not(:nth-child(1)):not(:nth-child(2)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .garden-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .garden-table .ant-input-number {
          width: 100%;
        }

        .garden-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .garden-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .garden-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .garden-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .garden-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Column Configuration Modal */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          // Reset temp values back to actual values when modal closes without applying
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setConfigModalVisible(false);
        }}
        onApply={handleConfigApply}
        width={500}
        isViewMode={tender.isViewMode}
      >
        <div className="grid grid-cols-2 gap-3">
          {/* xxxmm thick 'no dig' layer */}
          <div>
            <Text className="text-xs mb-1 block">
              xxxmm thick 'no dig' layer (m²)
            </Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempDimensionValues["thickNoDigLayer"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["thickNoDigLayer"];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders["thickNoDigLayer"];
                    return newHeaders;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    thickNoDigLayer: mmValue,
                  }));
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    thickNoDigLayer: `${mmValue}mm thick 'no dig' layer (m²)`,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
          {/* Spread and level xxmm clean subsoil cap (site won) */}
          <div>
            <Text className="text-xs mb-1 block">
              Clean subsoil cap (site won) (m²)
            </Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempDimensionValues["spreadLevelCleanSubsoilCapSiteWon"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["spreadLevelCleanSubsoilCapSiteWon"];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders["spreadLevelCleanSubsoilCapSiteWon"];
                    return newHeaders;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    spreadLevelCleanSubsoilCapSiteWon: mmValue,
                  }));
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    spreadLevelCleanSubsoilCapSiteWon: `Spread and level ${mmValue}mm clean subsoil cap (site won) (m²)`,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
          {/* Spread and level xxmm clean subsoil cap (imported) */}
          <div>
            <Text className="text-xs mb-1 block">
              Clean subsoil cap (imported) (m²)
            </Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempDimensionValues["spreadLevelCleanSubsoilCapImported"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["spreadLevelCleanSubsoilCapImported"];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders["spreadLevelCleanSubsoilCapImported"];
                    return newHeaders;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    spreadLevelCleanSubsoilCapImported: mmValue,
                  }));
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    spreadLevelCleanSubsoilCapImported: `Spread and level ${mmValue}mm clean subsoil cap (imported) (m²)`,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
          {/* Spread and level xxxmm topsoil (site won) */}
          <div>
            <Text className="text-xs mb-1 block">Topsoil (site won) (m²)</Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempDimensionValues["spreadLevelTopsoilSiteWon"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["spreadLevelTopsoilSiteWon"];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders["spreadLevelTopsoilSiteWon"];
                    return newHeaders;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    spreadLevelTopsoilSiteWon: mmValue,
                  }));
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    spreadLevelTopsoilSiteWon: `Spread and level ${mmValue}mm topsoil (site won) (m²)`,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
          {/* Spread and level xxmm topsoil (imported) */}
          <div>
            <Text className="text-xs mb-1 block">Topsoil (imported) (m²)</Text>
            <NumberInput
              value={(() => {
                const raw = parseFloat(tempDimensionValues["spreadLevelTopsoilImported"]) || 0;
                return raw >= 1 ? raw / 1000 : raw;
              })()}
              onChange={(value) => {
                const metresValue = value ? parseFloat(value.toString()) : 0;
                if (!metresValue) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues["spreadLevelTopsoilImported"];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders["spreadLevelTopsoilImported"];
                    return newHeaders;
                  });
                } else {
                  const mmValue = String(Math.round(metresValue * 1000));
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    spreadLevelTopsoilImported: mmValue,
                  }));
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    spreadLevelTopsoilImported: `Spread and level ${mmValue}mm topsoil (imported) (m²)`,
                  }));
                }
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
            />
          </div>
        </div>
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={keysToDelete.length > 1 ? `Are you sure you want to delete ${keysToDelete.length} records?` : undefined}
      />
    </MainLayout>
  );
};

export default Garden;
